/**
 * Tarjeta «Zona y periodo» de Pricing.
 *
 * Compartida por Dashboard y Estadísticas. Arranca **plegada**: en la cabecera
 * se ve un resumen (alcance y periodo activos) y el botón «Cambiar filtros»;
 * al desplegarla aparecen los cinco pasos (departamento, provincia, distrito,
 * año y mes) con la ayuda de cuántas opciones hay en cada nivel.
 */

import { ChevronDown, Filter, MapPin, RotateCcw } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { cn, formatNumber } from '@/lib/utils';

import type { FiltrosUbicacion } from '../hooks/use-filtros-ubicacion';
import { MESES } from '../lib/periodos';

const CAMPO =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-left text-sm ' +
  'text-ink-900 transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 ' +
  'focus:outline-none';

/** Paso numerado del filtro. */
function Paso({
  numero,
  htmlFor,
  etiqueta,
  ayuda,
  children,
}: {
  numero: number;
  htmlFor: string;
  etiqueta: string;
  ayuda: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="flex items-center gap-2 text-sm font-medium text-ink-700">
        <span
          className="flex size-5 items-center justify-center rounded-full bg-brand-100 text-[11px] font-bold text-brand-700"
          aria-hidden="true"
        >
          {numero}
        </span>
        {etiqueta}
      </label>

      {children}

      <span className="text-[11px] text-ink-400">{ayuda}</span>
    </div>
  );
}

interface FiltrosUbicacionCardProps {
  valores: FiltrosUbicacion;
  /** Años con lecturas de la zona elegida (`datos.anios`). */
  anios: number[];
  /** Departamentos con datos, para los chips de acceso rápido. */
  departamentosConDatos?: string[];
  /** Deja los cinco pasos visibles desde el principio. */
  abiertoPorDefecto?: boolean;
}

export function FiltrosUbicacionCard({
  valores,
  anios,
  departamentosConDatos = [],
  abiertoPorDefecto = false,
}: FiltrosUbicacionCardProps) {
  const { ubigeo, anio, mes } = valores;

  const [abierto, setAbierto] = useState(abiertoPorDefecto);
  const idPanel = `filtros-pricing-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  const departamento = ubigeo.departamentos;
  const provincia = ubigeo.provincias;
  const distrito = ubigeo.distritos;

  return (
    <Card>
      <CardContent className={cn(abierto ? 'space-y-4' : 'py-3')}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setAbierto((previo) => !previo)}
            aria-expanded={abierto}
            aria-controls={idPanel}
            className="flex min-w-0 items-center gap-2 text-sm font-semibold text-ink-900 transition-colors hover:text-brand-700"
          >
            <Filter className="size-4 shrink-0 text-brand-500" aria-hidden="true" />
            <span className="truncate">Zona y periodo</span>
            <ChevronDown
              className={cn(
                'size-4 shrink-0 text-ink-400 transition-transform',
                abierto && 'rotate-180',
              )}
              aria-hidden="true"
            />
          </button>

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="brand">
              <MapPin className="mr-1 size-3" aria-hidden="true" />
              {valores.alcance}
            </Badge>
            {valores.periodo ? <Badge variant="outline">{valores.periodo}</Badge> : null}

            {abierto ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={valores.limpiar}
                disabled={!valores.hayFiltro}
                title="Quitar zona y periodo y ver todo el histórico"
              >
                <RotateCcw className="mr-1.5 size-3.5" aria-hidden="true" />
                Limpiar filtros
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setAbierto(true)}>
                Cambiar filtros
              </Button>
            )}
          </div>
        </div>

        {abierto ? (
          <div id={idPanel} className="space-y-4">
            <p className="text-xs text-ink-500">
              Elige la zona (departamento, luego provincia y distrito) y, si quieres, acota el
              periodo. Puedes quedarte en el nivel que necesites: todo el país, solo Lima, solo
              Barranca o un distrito.
            </p>

            <div className="grid gap-3 sm:grid-cols-3">
              <Paso
                numero={1}
                htmlFor="filtro-departamento"
                etiqueta="Departamento"
                ayuda={
                  ubigeo.cargando
                    ? 'Cargando catálogo…'
                    : `${formatNumber(departamento.length)} departamentos disponibles`
                }
              >
                <Select
                  id="filtro-departamento"
                  value={ubigeo.departamentoId}
                  onChange={(evento) => ubigeo.elegirDepartamento(evento.target.value)}
                  disabled={ubigeo.cargando || departamento.length === 0}
                  searchable
                  className={CAMPO}
                >
                  <option value="">Todo el país</option>
                  {departamento.map((opcion) => (
                    <option key={opcion.id} value={opcion.id}>
                      {opcion.name}
                    </option>
                  ))}
                </Select>
              </Paso>

              <Paso
                numero={2}
                htmlFor="filtro-provincia"
                etiqueta="Provincia"
                ayuda={
                  ubigeo.departamentoId
                    ? `${formatNumber(provincia.length)} provincias en el departamento`
                    : 'Elige primero un departamento'
                }
              >
                <Select
                  id="filtro-provincia"
                  value={ubigeo.provinciaId}
                  onChange={(evento) => ubigeo.elegirProvincia(evento.target.value)}
                  disabled={!ubigeo.departamentoId || provincia.length === 0}
                  searchable
                  className={CAMPO}
                >
                  <option value="">Todas las provincias</option>
                  {provincia.map((opcion) => (
                    <option key={opcion.id} value={opcion.id}>
                      {opcion.name}
                    </option>
                  ))}
                </Select>
              </Paso>

              <Paso
                numero={3}
                htmlFor="filtro-distrito"
                etiqueta="Distrito"
                ayuda={
                  ubigeo.provinciaId
                    ? `${formatNumber(distrito.length)} distritos en la provincia`
                    : 'Elige primero una provincia'
                }
              >
                <Select
                  id="filtro-distrito"
                  value={ubigeo.distritoId}
                  onChange={(evento) => ubigeo.elegirDistrito(evento.target.value)}
                  disabled={!ubigeo.provinciaId || distrito.length === 0}
                  searchable
                  className={CAMPO}
                >
                  <option value="">Todos los distritos</option>
                  {distrito.map((opcion) => (
                    <option key={opcion.id} value={opcion.id}>
                      {opcion.name}
                    </option>
                  ))}
                </Select>
              </Paso>
            </div>

            <div className="border-t border-ink-100 pt-4">
              <h3 className="text-sm font-semibold text-ink-900">Periodo (opcional)</h3>
              <p className="mt-0.5 mb-3 text-xs text-ink-500">
                Sin año se analiza todo el histórico. Con año y mes puedes mirar un tramo concreto,
                por ejemplo octubre de 2026 frente al resto del año.
              </p>

              <div className="grid gap-3 sm:grid-cols-3">
                <Paso
                  numero={4}
                  htmlFor="filtro-anio"
                  etiqueta="Año"
                  ayuda={`${formatNumber(anios.length)} ${
                    anios.length === 1 ? 'año' : 'años'
                  } con lecturas`}
                >
                  <Select
                    id="filtro-anio"
                    value={anio}
                    onChange={(evento) => valores.setAnio(evento.target.value)}
                    disabled={anios.length === 0}
                    searchable
                    className={CAMPO}
                  >
                    <option value="">Todos los años</option>
                    {anios.map((valor) => (
                      <option key={valor} value={String(valor)}>
                        {valor}
                      </option>
                    ))}
                  </Select>
                </Paso>

                <Paso
                  numero={5}
                  htmlFor="filtro-mes"
                  etiqueta="Mes"
                  ayuda={anio ? 'Un mes concreto del año' : 'Elige primero un año'}
                >
                  <Select
                    id="filtro-mes"
                    value={mes}
                    onChange={(evento) => valores.setMes(evento.target.value)}
                    disabled={!anio}
                    searchable
                    className={CAMPO}
                  >
                    <option value="">Todo el año</option>
                    {MESES.map(([valor, nombre]) => (
                      <option key={valor} value={valor}>
                        {nombre}
                      </option>
                    ))}
                  </Select>
                </Paso>
              </div>
            </div>

            {ubigeo.error ? <p className="text-xs text-rose-600">{ubigeo.error}</p> : null}

            {!ubigeo.departamentoId && departamentosConDatos.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                  Con datos disponibles:
                </span>
                {departamentosConDatos.map((nombre) => (
                  <button
                    key={nombre}
                    type="button"
                    onClick={() => valores.irADepartamento(nombre)}
                    className="inline-flex items-center gap-1 rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 transition-colors hover:border-brand-300 hover:bg-brand-100"
                  >
                    <MapPin className="size-3" aria-hidden="true" />
                    {nombre}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
